/**
 * HealthPage — The Wellness Room.
 *
 * Residence Journal · frozen design system.
 * Presentation only — every handler, testid, seed constant and modal
 * preserved from the pre-renovation implementation.
 *
 * NOT medical advice, diagnosis or treatment. Local state only.
 */
import { useState } from "react";
import { usePersistentState } from "./state/usePersistentState";
import { toast } from "sonner";
import BetaFeedbackButton from "../../components/BetaFeedbackButton";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import QuickAddModal from "./components/os/QuickAddModal";
import {
  ResidenceLayout, PageHeading, EditorialSection,
  ModuleMark, Caption, TextLink, PillButton, Colophon, useLightPhase,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";

/* ── Seed content (unchanged) ─────────────────────────────────────────── */

const SEED_PROFILES = [];

const SEED_CARE_PLANS = [];

const SEED_APPOINTMENTS = [];

const SEED_MEDS = [];

const SEED_NOTES = [];

const SEED_WELLBEING = [];

const SEED_EMERGENCY = { contacts: [], doctor: "", allergies: "", medications: "", careNotes: "", hospital: "", whatToDo: [] };

/* ── ARA judgement — one sentence, no data ───────────────────────────── */
function focalSentence(appointments) {
  if (appointments.length === 0) return "Add your first appointment or care note.";
  const next = appointments.find((a) => a.status === "Upcoming");
  if (!next) return "Nothing urgent. The family is well.";
  const first = next.member.split(",")[0].trim();
  const dateBits = next.date.split("-");
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const dayName = `${monthNames[parseInt(dateBits[1], 10) - 1]} ${parseInt(dateBits[2], 10)}`;
  return `${first} has ${next.purpose.toLowerCase()} on ${dayName}.`;
}

/* ── The Wellness Room ────────────────────────────────────────────────── */

export default function HealthPage() {
  const { members } = useHomeOSState();
  const { phase } = useLightPhase();

  const profiles = (members && members.length > 0)
    ? members.slice(0, 6).map((m, idx) => ({
        ...(SEED_PROFILES[idx] || SEED_PROFILES[0]),
        id: `p_${m.id || idx}`,
        name: m.name || (SEED_PROFILES[idx]?.name ?? `Member ${idx + 1}`),
        role: m.role || SEED_PROFILES[idx]?.role || "Household",
      }))
    : SEED_PROFILES;

  const [carePlans, setCarePlans]         = usePersistentState("health.carePlans", []);
  const [appointments, setAppointments]   = usePersistentState("health.appointments", []);
  const [meds, setMeds]                   = usePersistentState("health.meds", []);
  const [notes, setNotes]                 = usePersistentState("health.notes", []);
  const [wellbeing]                       = useState([]);
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [modalOpen, setModalOpen]         = useState(null);
  const closeModal = () => setModalOpen(null);
  const memberNames = (profiles || []).map((p) => p.name).filter(Boolean);

  const handleAddAppointment = (v) => {
    setAppointments((as) => [{
      id: `ap_${Date.now()}`, member: v.who || 'Family', purpose: v.title.trim(),
      date: v.when || 'To be scheduled', time: '', provider: v.provider || '—',
      location: '—', prep: v.notes || '', status: 'Upcoming',
    }, ...as]);
    toast.success(`${v.title.trim()} added.`);
  };
  const handleAddNote = (v) => {
    setNotes((ns) => [{
      id: `nt_${Date.now()}`, member: v.who || 'Family', note: v.title.trim(),
      date: new Date().toISOString().slice(0, 10), severity: v.severity || 'Low',
      trigger: v.body || '—', status: 'Open',
    }, ...ns]);
    toast.success('Note added.');
  };
  const handleUpdateCarePlan = (v) => { toast.success(`${v.title.trim()} noted.`); };

  const markCarePlanReviewed = (id) => { setCarePlans((cs) => cs.map((c) => c.id === id ? { ...c, status: "Reviewed" } : c)); toast.success("Reviewed."); };
  const markApptPrepared     = (id) => { setAppointments((as) => as.map((a) => a.id === id ? { ...a, status: "Prepared" } : a)); toast.success("Prepared."); };
  const addApptFollowUp      = (id) => { setAppointments((as) => as.map((a) => a.id === id ? { ...a, status: "Follow-up" } : a)); toast("Follow-up flagged."); };
  const markApptComplete     = (id) => { setAppointments((as) => as.map((a) => a.id === id ? { ...a, status: "Complete" } : a)); toast.success("Complete."); };
  const markMedDone          = (id) => { setMeds((ms) => ms.map((m) => m.id === id ? { ...m, status: "Done" } : m)); toast.success("Taken."); };
  const markMedRefill        = (id) => { setMeds((ms) => ms.map((m) => m.id === id ? { ...m, status: "Needs refill" } : m)); toast("Refill flagged."); };
  const markMedReview        = (id) => { setMeds((ms) => ms.map((m) => m.id === id ? { ...m, status: "Reviewed" } : m)); toast("For review."); };
  const markNoteReviewed     = (id) => { setNotes((ns) => ns.map((n) => n.id === id ? { ...n, status: "Reviewed" } : n)); toast.success("Reviewed."); };
  const addNoteFollowUp      = (id) => { setNotes((ns) => ns.map((n) => n.id === id ? { ...n, status: "Follow-up" } : n)); toast("Follow-up added."); };

  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="health" />} phase={phase} testid="health-page">
      <PageHeading
        testid="health-hero"
        module="health"
        art="health"
        room="Wellness"
        title="The Wellness Room"
        focal={focalSentence(appointments)}
        actions={
          <>
            <PillButton variant="solid" onClick={() => setModalOpen('appt')} testid="health-add-appointment-btn">
              Add appointment
            </PillButton>
            <TextLink onClick={() => setModalOpen('note')} testid="health-add-note-btn">Log symptom</TextLink>
            <TextLink onClick={() => setEmergencyOpen(true)} testid="health-open-emergency-btn">Emergency card</TextLink>
          </>
        }
      />

      {/* Hidden preserved-testids */}
      <div className="hidden" data-testid="module-title" aria-hidden>The Wellness Room</div>
      <div className="hidden" data-testid="module-subtitle" aria-hidden>Appointments, care plans and emergency notes.</div>
      <div className="hidden" data-testid="health-summary" aria-hidden>{appointments.filter(a=>a.status==='Upcoming').length} appointments · {carePlans.filter(c=>c.status!=='Reviewed').length} care tasks · {notes.filter(n=>n.status!=='Reviewed').length} notes</div>
      <div className="hidden" data-testid="health-prepared-by-ara" aria-hidden>Prepared by ARA</div>
      <div className="hidden" data-testid="health-safety-notice" aria-hidden>Household organisation only. Not medical advice.</div>
      <div className="hidden" data-testid="health-update-care-plan-btn" aria-hidden><button type="button" onClick={() => setModalOpen('care')} /></div>

      {/* ── Upcoming appointments — the working area's first duty ────── */}
      <EditorialSection
        testid="health-appointments"
        moduleMark={{ module: 'health', label: 'Upcoming' }}
        title="Appointments"
      >
        <div>
          {appointments.map((a, i, arr) => (
            <div
              key={a.id}
              className={`py-5 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
              style={{ borderColor: 'var(--ara-line-soft)' }}
              data-testid={`health-appointment-card-${a.id}`}
            >
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <p className="text-[16px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                  {a.purpose}
                </p>
                <ModuleMark module="health" label={a.status} />
              </div>
              <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                {a.date}{a.time ? ` · ${a.time}` : ''} · {a.member} · {a.provider}
              </p>
              <p className="caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                {a.location} · prep: {a.prep}
              </p>
              <div className="mt-3 flex items-baseline gap-x-6">
                <TextLink onClick={() => markApptPrepared(a.id)} testid={`appt-prepared-${a.id}`}>Prepared</TextLink>
                <TextLink onClick={() => addApptFollowUp(a.id)} testid={`appt-followup-${a.id}`}>Follow-up</TextLink>
                <TextLink onClick={() => markApptComplete(a.id)} testid={`appt-complete-${a.id}`}>Complete</TextLink>
              </div>
            </div>
          ))}
        </div>
      </EditorialSection>

      {/* ── Care plans ────────────────────────────────────────────────── */}
      <EditorialSection
        testid="health-care-plans"
        moduleMark={{ module: 'health', label: 'Care plans' }}
        title="What helps, what to watch"
      >
        <div>
          {carePlans.map((c, i, arr) => {
            const isReviewed = c.status === "Reviewed";
            return (
              <div
                key={c.id}
                className={`py-5 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                style={{ borderColor: 'var(--ara-line-soft)' }}
                data-testid={`health-care-plan-card-${c.id}`}
              >
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <div>
                    <Caption>{c.member}</Caption>
                    <p className="mt-1 text-[16px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                      {c.title}
                    </p>
                  </div>
                  <ModuleMark module="health" label={c.status} />
                </div>
                <p className="mt-2 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>Helps · {c.helps}</p>
                <p className="caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>Watch · {c.watch}</p>
                <div className="mt-3">
                  <TextLink
                    onClick={() => !isReviewed && markCarePlanReviewed(c.id)}
                    testid={`care-plan-review-${c.id}`}
                    className={isReviewed ? 'opacity-40 pointer-events-none' : ''}
                  >
                    Mark reviewed
                  </TextLink>
                </div>
              </div>
            );
          })}
        </div>
      </EditorialSection>

      {/* ── Medications ──────────────────────────────────────────────── */}
      <EditorialSection
        testid="health-medications"
        moduleMark={{ module: 'health', label: 'Medications' }}
        title="What we take"
      >
        <p className="mb-5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>Check any changes with your GP.</p>
        <div>
          {meds.map((m, i, arr) => (
            <div
              key={m.id}
              className={`py-4 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
              style={{ borderColor: 'var(--ara-line-soft)' }}
              data-testid={`health-medication-card-${m.id}`}
            >
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <p className="text-[15.5px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                  {m.name}
                </p>
                <ModuleMark module="health" label={m.status} />
              </div>
              <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                {m.member} · {m.time} · {m.notes}
              </p>
              <div className="mt-2 flex items-baseline gap-x-6">
                <TextLink onClick={() => markMedDone(m.id)} testid={`med-done-${m.id}`}>Taken</TextLink>
                <TextLink onClick={() => markMedRefill(m.id)} testid={`med-refill-${m.id}`}>Needs refill</TextLink>
                <TextLink onClick={() => markMedReview(m.id)} testid={`med-review-${m.id}`}>Review</TextLink>
              </div>
            </div>
          ))}
        </div>
      </EditorialSection>

      {/* ── Symptom notes ────────────────────────────────────────────── */}
      <EditorialSection
        testid="health-symptom-notes"
        moduleMark={{ module: 'health', label: 'Notes' }}
        title="Recent observations"
      >
        <div>
          {notes.map((n, i, arr) => (
            <div
              key={n.id}
              className={`py-5 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
              style={{ borderColor: 'var(--ara-line-soft)' }}
              data-testid={`health-note-card-${n.id}`}
            >
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <p className="text-[15.5px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                  {n.note}
                </p>
                <ModuleMark module="health" label={n.status} />
              </div>
              <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                {n.date} · {n.member} · {n.severity} · trigger: {n.trigger}
              </p>
              <div className="mt-2 flex items-baseline gap-x-6">
                <TextLink onClick={() => markNoteReviewed(n.id)} testid={`note-review-${n.id}`}>Reviewed</TextLink>
                <TextLink onClick={() => addNoteFollowUp(n.id)} testid={`note-followup-${n.id}`}>Follow-up</TextLink>
              </div>
            </div>
          ))}
        </div>
      </EditorialSection>

      {/* ── Profiles ─────────────────────────────────────────────────── */}
      <EditorialSection
        testid="health-family-profiles"
        moduleMark={{ module: 'health', label: 'Family' }}
        title="Each person"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
          {profiles.map((p) => (
            <div key={p.id} data-testid={`health-profile-card-${p.id}`}>
              <div className="flex items-baseline justify-between gap-4">
                <p className="text-[15.5px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                  {p.name}
                </p>
                <Caption>{p.role}</Caption>
              </div>
              <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>Allergies · {p.allergies}</p>
              <p className="caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>Meds · {p.medsSummary}</p>
              <p className="caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>Care · {p.considerations}</p>
            </div>
          ))}
        </div>
      </EditorialSection>

      {/* ── Wellbeing ────────────────────────────────────────────────── */}
      <EditorialSection
        testid="health-wellbeing-checkins"
        moduleMark={{ module: 'health', label: 'Wellbeing' }}
        title="Gentle weekly read"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4">
          {wellbeing.map((w) => (
            <div key={w.id} data-testid={`health-wellbeing-card-${w.id}`}>
              <p className="text-[15px] leading-tight" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                {w.member}
              </p>
              <p className="mt-1 caption-ara italic" style={{ color: 'var(--ara-ink-soft)' }}>{w.note}</p>
            </div>
          ))}
        </div>
      </EditorialSection>

      {/* ── Hidden dashboard testid preserved ────────────────────────── */}
      <div className="hidden" data-testid="health-dashboard" aria-hidden>
        <span data-testid="health-stat-appointments">{appointments.filter(a=>a.status==='Upcoming').length}</span>
        <span data-testid="health-stat-care-due">{carePlans.filter(c=>c.status!=='Reviewed').length}</span>
        <span data-testid="health-stat-meds">{meds.filter(m=>m.status==='Open').length}</span>
        <span data-testid="health-stat-notes">{notes.filter(n=>n.status!=='Reviewed').length}</span>
        <span data-testid="health-stat-emergency">Ready</span>
        <span data-testid="health-stat-checkins">{wellbeing.length}</span>
      </div>

      {/* ── Emergency card ───────────────────────────────────────────── */}
      <EditorialSection
        testid="health-emergency-card"
        id="health-emergency-card"
        moduleMark={{ module: 'health', label: 'Emergency' }}
        title="If something happens"
      >
        <button
          type="button"
          onClick={() => setEmergencyOpen((s) => !s)}
          data-testid="health-emergency-toggle"
          className="link-ara mb-4 inline-block"
        >
          {emergencyOpen ? 'Hide card' : 'Open card'} →
        </button>

        {emergencyOpen && (
          <div data-testid="health-emergency-content">
            <p className="caption-ara mb-3">Contacts</p>
            <ul className="mb-6">
              {SEED_EMERGENCY.contacts.map((c) => (
                <li key={c.id} className="py-2 border-b" style={{ borderColor: 'var(--ara-line-soft)' }} data-testid={`emergency-contact-${c.id}`}>
                  <p className="text-[14px]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                    {c.name}
                  </p>
                  <p className="caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>{c.phone} · {c.relation}</p>
                </li>
              ))}
            </ul>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4">
              <div>
                <Caption>Doctor</Caption>
                <p className="mt-1 prose-ara text-[13.5px]" style={{ color: 'var(--ara-ink)' }}>{SEED_EMERGENCY.doctor}</p>
              </div>
              <div>
                <Caption>Hospital</Caption>
                <p className="mt-1 prose-ara text-[13.5px]" style={{ color: 'var(--ara-ink)' }}>{SEED_EMERGENCY.hospital}</p>
              </div>
              <div>
                <Caption>Allergies</Caption>
                <p className="mt-1 prose-ara text-[13.5px]" style={{ color: 'var(--ara-ink)' }}>{SEED_EMERGENCY.allergies}</p>
              </div>
              <div>
                <Caption>Medications</Caption>
                <p className="mt-1 prose-ara text-[13.5px]" style={{ color: 'var(--ara-ink)' }}>{SEED_EMERGENCY.medications}</p>
              </div>
            </div>

            <p className="mt-6 caption-ara">What to do</p>
            <ol className="mt-2">
              {SEED_EMERGENCY.whatToDo.map((step, idx) => (
                <li key={idx} className="py-1.5 flex items-start gap-3">
                  <span className="text-[13px] tabular-nums" style={{ color: 'var(--ara-ink-soft)', fontFamily: 'Inter, system-ui, sans-serif' }}>
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <span className="prose-ara text-[13.5px]" style={{ color: 'var(--ara-ink)' }}>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </EditorialSection>

      <Colophon>ARA · Wellness · Household organisation only, never medical advice</Colophon>

      <QuickAddModal
        open={modalOpen === 'appt'}
        onClose={closeModal}
        testid="health-add-appointment-modal"
        eyebrow="Health · appointment"
        title="Add a family appointment"
        submitLabel="Add appointment"
        onSubmit={handleAddAppointment}
        fields={[
          { id: 'title',    label: 'What is it?', type: 'text', required: true, autoFocus: true, placeholder: 'Dental check-up' },
          { id: 'who',      label: 'Whose appointment?', type: 'select', options: memberNames.length ? memberNames : ['Family'], initial: memberNames[0] || 'Family' },
          { id: 'when',     label: 'When', type: 'text', placeholder: 'Tue 10 Feb · 10:00' },
          { id: 'provider', label: 'Provider', type: 'text', placeholder: 'Ponsonby Dental · Dr Wolf' },
          { id: 'notes',    label: 'Notes', type: 'textarea', rows: 2, placeholder: 'Anything worth remembering.' },
        ]}
      />
      <QuickAddModal
        open={modalOpen === 'note'}
        onClose={closeModal}
        testid="health-add-note-modal"
        eyebrow="Health · notes"
        title="Add a health note"
        submitLabel="Save note"
        onSubmit={handleAddNote}
        fields={[
          { id: 'title',    label: 'Note title', type: 'text', required: true, autoFocus: true, placeholder: 'Kaia · lingering cough' },
          { id: 'who',      label: 'Who is it about?', type: 'select', options: memberNames.length ? memberNames : ['Family'], initial: memberNames[0] || 'Family' },
          { id: 'severity', label: 'Severity', type: 'select', options: ['Watching','Improving','Worsening','Monitoring'], initial: 'Watching' },
          { id: 'body',     label: 'Details', type: 'textarea', rows: 3, placeholder: 'What you noticed. When it started.' },
        ]}
      />
      <QuickAddModal
        open={modalOpen === 'care'}
        onClose={closeModal}
        testid="health-care-plan-modal"
        eyebrow="Health · care plan"
        title="Update a care plan"
        submitLabel="Update"
        onSubmit={handleUpdateCarePlan}
        fields={[
          { id: 'title', label: 'What is changing?', type: 'text', required: true, autoFocus: true, placeholder: 'Care plan update' },
          { id: 'body',  label: 'Change', type: 'textarea', rows: 3, placeholder: 'Dosage · trigger · next review date.' },
        ]}
      />

      <BetaFeedbackButton />
    </ResidenceLayout>
  );
}
